import React, { useMemo } from "react";
import styles from "../../_css/Pricing.module.css";
import { usePricing } from "./PricingContext";
import { TIER_COLORS, TIER_IDS, PUBLISH_CONFIG_OPTIONS } from "./mockData";
import {
  computeTierCapacity,
  formatEffectiveFromDisplay,
  PUBLISH_SCOPE_OPTIONS,
} from "./pricingTransforms";
import {
  PageHead,
  SectionTitle,
  StatusPill,
  TierPill,
  EuroInput,
  CreditInput,
  NumInput,
  DateInput,
  ToggleSwitch,
  Panel,
  ActionsBar,
  Select,
  MultiSelect,
  Btn,
} from "./shared";

// Caption under the heading: which version new contracts get right now, and
// which published versions are still waiting for their effective date.
function describeRateCardVersion(version) {
  if (!version) return [];

  let current =
    version.plansEditId != null
      ? `Price list for new contracts: v${version.plansEditId}, effective since ${
          version.plansEffectiveFrom
            ? formatEffectiveFromDisplay(version.plansEffectiveFrom)
            : "—"
        }`
      : "Base defaults (no published version in effect)";
  if (version.ratesEditId !== version.plansEditId) {
    current += ` · meter rates ${
      version.ratesEditId != null ? `v${version.ratesEditId}` : "base defaults"
    }`;
  }

  return [
    current,
    ...(version.scheduled || []).map(
      (item) => `${item.version} scheduled from ${item.date}`,
    ),
  ];
}

export default function RateCardSection() {
  const {
    planTiers,
    setPlanTiers,
    changeHistory,
    models,
    modelToggles,
    setModelToggles,
    meterRateRows,
    meterRates,
    setMeterRates,
    effectiveFrom,
    setEffectiveFrom,
    publishScope,
    setPublishScope,
    publishConfig,
    setPublishConfig,
    setPublishModalOpen,
    loading,
    loadError,
    reloadRateCard,
    rateCardVersion,
  } = usePricing();

  const capacityByTier = useMemo(
    () =>
      Object.fromEntries(
        TIER_IDS.map((tierId) => {
          const planTier = planTiers.find((t) => t.id === tierId);
          return [
            tierId,
            planTier
              ? computeTierCapacity({
                  tierId,
                  planTier,
                  meterRateRows,
                  meterRates,
                  modelToggles,
                  models,
                })
              : null,
          ];
        }),
      ),
    [planTiers, meterRateRows, meterRates, modelToggles, models],
  );

  const updateTier = (id, field, value) => {
    setPlanTiers((prev) =>
      prev.map((tier) => (tier.id === id ? { ...tier, [field]: value } : tier)),
    );
  };

  const updateMeter = (id, tier, value) => {
    setMeterRates((prev) => ({
      ...prev,
      [id]: { ...prev[id], [tier]: value },
    }));
  };

  const toggleModel = (provider, tier) => {
    setModelToggles((prev) => ({
      ...prev,
      [provider]: { ...prev[provider], [tier]: !prev[provider][tier] },
    }));
  };

  if (loading) {
    return (
      <>
        <PageHead title="Rate card" subtitle="Global pricing defaults" />
        <Panel>
          <p className={styles.muted}>Loading rate card…</p>
        </Panel>
      </>
    );
  }

  if (loadError) {
    return (
      <>
        <PageHead title="Rate card" subtitle="Global pricing defaults" />
        <Panel>
          <p className={styles.muted}>{loadError}</p>
          <ActionsBar>
            <Btn primary sm onClick={reloadRateCard}>
              Retry
            </Btn>
          </ActionsBar>
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHead title="Rate card" subtitle="Global pricing defaults" />
      <p className={styles.sectionHint}>
        {describeRateCardVersion(rateCardVersion).map((line, index) => (
          <React.Fragment key={line}>
            {index > 0 && <br />}
            {line}
          </React.Fragment>
        ))}
      </p>

      <SectionTitle>Plan templates</SectionTitle>
      <Panel>
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: "16%" }}>Tier</th>
              <th className={styles.alignRight}>Package / mo</th>
              <th className={styles.alignRight}>Included credits / mo</th>
              <th className={styles.alignRight}>€ / credit</th>
              <th>Models</th>
            </tr>
          </thead>
          <tbody>
            {planTiers.map((tier) => (
              <tr key={tier.id}>
                <td>
                  <b>{tier.id}</b> <TierPill tierId={tier.id} />
                </td>
                <td className={styles.alignRight}>
                  <EuroInput
                    value={tier.packageMo}
                    onChange={(v) => updateTier(tier.id, "packageMo", v)}
                  />
                </td>
                <td className={styles.alignRight}>
                  <CreditInput
                    value={tier.includedCredits}
                    onChange={(v) => updateTier(tier.id, "includedCredits", v)}
                  />
                </td>
                <td className={`${styles.alignRight} ${styles.muted}`}>
                  {capacityByTier[tier.id]?.perCredit || "—"}
                </td>
                <td>
                  <span className={styles.tag}>
                    {capacityByTier[tier.id]?.modelsLabel || "—"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <SectionTitle>Models per tier</SectionTitle>
      <Panel>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Provider</th>
              {TIER_IDS.map((tier) => (
                <th
                  key={tier}
                  className={styles.alignRight}
                  style={{ width: "14%" }}
                >
                  {tier}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {models.map((model) => (
              <tr key={model.id}>
                <td>
                  <b>{model.provider}</b>
                </td>
                {TIER_IDS.map((tier) => (
                  <td key={tier} className={styles.alignRight}>
                    <ToggleSwitch
                      on={modelToggles[model.provider]?.[tier] ?? false}
                      onToggle={() => toggleModel(model.provider, tier)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <SectionTitle>Meter rates</SectionTitle>
      <Panel>
        <table className={`${styles.table} ${styles.meterTable}`}>
          <thead>
            <tr>
              <th style={{ width: "32%" }}>Action</th>
              <th style={{ width: "23%" }}>Trigger</th>
              {TIER_IDS.map((tier) => (
                <th
                  key={tier}
                  className={styles.alignRight}
                  style={{ width: "15%" }}
                >
                  {tier} · cr
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {meterRateRows.length === 0 ? (
              <tr>
                <td colSpan={5} className={styles.muted}>
                  No meter rates configured.
                </td>
              </tr>
            ) : (
              meterRateRows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <b>{row.action}</b>
                  </td>
                  <td>
                    <span className={styles.muted}>{row.trigger}</span>
                  </td>
                  {TIER_IDS.map((tier) => (
                    <td key={tier} className={styles.alignRight}>
                      <NumInput
                        value={meterRates[row.id]?.[tier] ?? ""}
                        onChange={(v) => updateMeter(row.id, tier, v)}
                      />
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
        <ActionsBar>
          <span className={styles.muted} style={{ fontSize: 12 }}>
            Full simulation / per re-run is billed per LLM run. A run executes
            one query per enabled model.
          </span>
        </ActionsBar>
      </Panel>

      <Panel className={styles.publishPanel}>
        <div className={styles.setRow}>
          <div className={styles.setRowLabel}>
            Publish edits
            <small>
              Saves and publishes every change on this page to the active rate
              card.
            </small>
          </div>
          <div className={styles.publishControls}>
            <MultiSelect
              value={publishConfig}
              onChange={setPublishConfig}
              options={PUBLISH_CONFIG_OPTIONS}
              placeholder="Select config"
            />
            <div className={styles.publishFieldGroup}>
              <span className={styles.publishFieldLabel}>Effective from</span>
              <DateInput value={effectiveFrom} onChange={setEffectiveFrom} />
            </div>
            <Select
              value={publishScope}
              onChange={setPublishScope}
              options={PUBLISH_SCOPE_OPTIONS}
            />
            <Btn primary sm onClick={() => setPublishModalOpen(true)}>
              Publish
            </Btn>
          </div>
        </div>
      </Panel>

      <SectionTitle>Capacity preview</SectionTitle>
      <p className={styles.sectionHint}>
        What each tier&apos;s included credits buy at the current rate card,
        calculated live.
      </p>
      <div className={styles.previewGrid}>
        {TIER_IDS.map((tierId) => {
          const cap = capacityByTier[tierId];
          return (
            <Panel key={tierId} className={styles.previewCard}>
              <h4>
                <span
                  className={styles.previewDot}
                  style={{ background: TIER_COLORS[tierId] }}
                />
                <span>
                  {tierId} · <b>{cap?.included ?? "—"}</b> credits
                </span>
              </h4>
              <ul className={styles.previewList}>
                {(cap?.meterRateRows || []).map((row) => (
                  <li key={row.id}>
                    {row.action}
                    <b>{cap?.actions?.[row.id] ?? "—"}</b>
                  </li>
                ))}
              </ul>
              <div className={styles.previewNote}>{cap?.note || "—"}</div>
            </Panel>
          );
        })}
      </div>

      <SectionTitle>Change history</SectionTitle>
      <Panel>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Version</th>
              <th>Changed by</th>
              <th>Note</th>
              <th className={styles.alignRight}>Effective date</th>
              <th className={styles.alignRight}>Created at</th>
            </tr>
          </thead>
          <tbody>
            {changeHistory.length === 0 ? (
              <tr>
                <td colSpan={5} className={styles.muted}>
                  No change history yet.
                </td>
              </tr>
            ) : (
              changeHistory.map((row) => (
                <tr key={row.id ?? `${row.version}-${row.date}`}>
                  <td>
                    <b>{row.version}</b> <StatusPill status={row.status} />
                  </td>
                  <td>{row.changedBy}</td>
                  <td className={styles.muted}>{row.note}</td>
                  <td className={`${styles.alignRight} ${styles.muted}`}>
                    {row.date}
                  </td>
                  <td className={`${styles.alignRight} ${styles.muted}`}>
                    {row.createdAt}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Panel>
    </>
  );
}
